import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, MyThunkDispatch } from 'store';

import { createInviteOrUpdateUser } from 'actions/adminUser';
import { fetchNationalityCountryList } from 'actions/i18n';
import { fetchUserAccount, getAccount as refreshAccount } from 'actions/user';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { AdminUser } from 'models/adminUser';
import { upload, getPhoto } from 'services/photoProfileService';
import { extractErrorMessage, base64ToFile } from 'utils/generic';

import TravelerFormProfile from '../components/TravelerFormProfile';

const TravelerProfileEditContainer = () => {
  const dispatch: MyThunkDispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState('');
  const toast = useToast();
  const { t } = useStoreTranslation();

  const {
    user: { accountResult },
    i18n: { loadingNationalityCountries, nationalityCountries },
  } = useSelector((state: RootState) => state);

  const getAccount = () => dispatch(fetchUserAccount());

  const fetchTravelerUser = useCallback(
    async () => {
      try {
        setIsLoading(true);
        await getAccount();

        try {
          const responsePhoto = await getPhoto();

          if (responsePhoto.succeeded && responsePhoto.data.picture) {
            const file = await base64ToFile(
              `data:text/plain;base64,${responsePhoto.data.picture}` as string,
              responsePhoto.data.extension,
            );
            const reader = new FileReader();
            reader.readAsDataURL(file as File);
            reader.onload = () => {
              setProfilePhoto(reader.result as string);
            };
          }
        } catch (e) {}
      } catch (error) {
        toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const companyId = accountResult ? accountResult.companyId : '';
  const id = accountResult ? accountResult.id : '';

  useEffect(() => {
    fetchTravelerUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const user = useUserAccount();

  const data = {
    id,
    user,
    isLoading,
    companyId,
    nationalityCountries,
    loadingNationalityCountries,
  };

  const handleSaveUser = useCallback(
    (user: AdminUser, companyId: string, userId: string) =>
      new Promise<void>(async (resolve, reject) => {
        try {
          await dispatch(createInviteOrUpdateUser(user, companyId, userId));
          await dispatch(refreshAccount());
          resolve();
        } catch (ex) {
          reject(ex);
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleSaveImageProfile = useCallback(async (data: FormData) => {
    data.append('userid', user?.id ?? '');
    await upload(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TravelerFormProfile
      userPhoto={profilePhoto}
      {...data}
      fetchCountryList={() => dispatch(fetchNationalityCountryList())}
      saveUser={handleSaveUser}
      saveProfileImage={handleSaveImageProfile}
    />
  );
};

export default TravelerProfileEditContainer;
